import moment from 'moment/moment';
import { ConfigField } from '@/eventConfig/types.ts';
import { FieldType } from '@/form/FormProvider.tsx';
import { isNil } from 'ramda';

const metricUnitMultiply = (value: string, multiplier: string, minVal: string) => {
  // Return 0 if value is empty or undefined
  if (!value) {
    return 0;
  }

  // Replace commas with dots for decimal conversion
  value = value.replace(',', '.');
  const minValFloat = parseFloat(minVal) || 0.0;

  // Parse the value as a float
  let parsedValue = parseFloat(value);
  if (isNaN(parsedValue)) {
    return 0; // Return 0 if parsing fails
  }

  // Adjust the parsedValue if it's less than minVal
  const indexOfDot = value.indexOf('.');
  if (indexOfDot > -1) {
    let decimalPlaces = value.length - indexOfDot - 1;
    while (decimalPlaces-- > 0 && parsedValue < minValFloat) {
      parsedValue *= 10;
    }
  }

  // Parse the multiplier, defaulting to 1.0 if undefined
  const parsedMultiplier = parseFloat(multiplier) || 1.0;

  // Multiply the parsed value by the multiplier
  const result = parsedValue * parsedMultiplier;

  // Return the result as an integer
  return Math.floor(result);
};

const ftin = (value: string, multiplier: string, minVal: string) => {
  if (value.includes("'")) {
    let feet = 0;
    let inch = 0;
    const split = value.split("'");
    feet = parseInt(split[0]);
    inch = split[1] ? parseInt(split[1].replace('"', '').replace("'", '')) : 0; // we accept 5'6, 5'6", 5'6''
    const heightInCm = (feet * 0.3048 + inch * 0.0254) * 100;
    return parseInt(`${heightInCm}`);
  }
  return metricUnitMultiply(value, multiplier, minVal);
};

const float = (value: string) => {
  /** honor local number formats (digit separator, etc...) */
  if (value.match(/^\d+[,.]\d{2}$/)) {
    value = value.replace(/[,.]+/g, '.');
  }

  return parseFloat(value);
};

export const normalize = (datatype: ConfigField['datatype'], value: FieldType | undefined): FieldType => {
  if (isNil(value)) {
    return null;
  }
  if (!datatype) {
    return value;
  }
  if (typeof value !== 'string') {
    return value;
  }

  const [type, arg0, arg1] = datatype.split(':');

  switch (type) {
    case 'string':
      return String(value);
    case 'int':
      return metricUnitMultiply(value, arg0, arg1);
    case 'ftin':
      return ftin(value, arg0, arg1);
    case 'float':
      return float(value);
    case 'bool':
      return !!value;
    case 'date':
      return moment(value, arg0).format('YYYY-MM-DD');
    case 'rut':
      return value.replace(/[^0-9kK-]+/g, '');

    default:
      return value;
  }
};
